/**
 * Moving a whole series.
 *
 * A bill, income or loan is a RULE, not a row, so "move every one of these"
 * means rewriting the rule to land on the new day. Each frequency stores that
 * day differently, and getting it wrong is silent: a weekly bill whose `dueDay`
 * is set to a day-of-MONTH still matches something, just the wrong thing.
 *
 * Biweekly is the odd one. Its day is not stored at all — the schedule is
 * anchored to a start date and counted in fourteens — so the anchor shifts by
 * the same number of days the occurrence moved, which preserves the cadence
 * rather than resetting it.
 */

import { Expense, Income, Loan } from '../../types';
import { startOfDay, toLocalDay } from './dates';

/** Whole days from `from` to `to`, via UTC so a DST boundary cannot round. */
export function dayDelta(from: Date, to: Date): number {
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b - a) / 86400000);
}

export function expenseSeriesPatch(expense: Expense, newDate: Date): Partial<Expense> {
  switch (expense.frequency) {
    case 'monthly':
      return { dueDay: newDate.getDate() };
    case 'weekly':
      return { dueDay: newDate.getDay() };
    case 'one-time':
      return { dueDate: newDate };
    default:
      return {};
  }
}

export function incomeSeriesPatch(
  income: Income,
  originalDate: Date,
  newDate: Date
): Partial<Income> {
  switch (income.frequency) {
    case 'monthly':
      return { payDay: newDate.getDate() };
    case 'weekly':
      return { payDay: newDate.getDay() };
    case 'biweekly': {
      if (!income.biweeklyStartDate) return {};
      const anchor = startOfDay(toLocalDay(income.biweeklyStartDate));
      const shifted = new Date(anchor);
      shifted.setDate(shifted.getDate() + dayDelta(originalDate, newDate));
      return { biweeklyStartDate: shifted };
    }
    case 'one-time':
      return { depositDate: newDate };
    default:
      return {};
  }
}

export function loanSeriesPatch(_loan: Loan, newDate: Date): Partial<Loan> {
  return { dueDay: newDate.getDate() };
}

/** Whether an item repeats — a one-off has only one occurrence, so there is
 *  nothing to ask about when it moves. */
export function isRecurring(frequency: string): boolean {
  return frequency !== 'one-time';
}
